import { InputModeOptions } from 'react-native';
import { RegistrationData } from '../registrationForm/registrationForm.types';
import { RegistrationConfigBase } from '../shared/registrationShared.types';
import { FieldConfig } from './registrationConfig.types';
import { RegistrationVariantEnum } from '../../utils/fetchRegistrationVariant';
import { NamespacedImageKeys } from '@bonanza-social/moonlight';

export const DefaultRegistrationConfig: RegistrationConfigBase = {
  id: RegistrationVariantEnum.elevenStep,
  countdownTimer: 900,
  startPercentage: 5,
  endPercentage: 95,
  steps: [
    {
      id: 'intro',
      disableHeader: true,
    },
    {
      id: 'offer',
      fields: ['promoId'] as (keyof RegistrationData)[],
    },
    {
      id: 'email',
      fields: ['email'] as (keyof RegistrationData)[],
    },
    {
      id: 'password',
      fields: ['password'] as (keyof RegistrationData)[],
      featureTip: {
        descriptionKey: 'passwordFeatureTip',
        image: 'landingPage.stars' as NamespacedImageKeys,
      },
    },
    {
      id: 'phone',
      fields: ['phone'] as (keyof RegistrationData)[],
      featureTip: {
        descriptionKey: 'phoneFeatureTip',
        image: 'common.BonanzaWheel',
      },
    },
    {
      id: 'names',
      fields: ['firstName', 'middleName'] as (keyof RegistrationData)[],
      featureTip: {
        descriptionKey: 'namesFeatureTip',
        image: 'common.BonanzaCoinStack',
      },
    },
    {
      id: 'lastName',
      fields: ['lastName'] as (keyof RegistrationData)[],
      featureTip: {
        descriptionKey: 'lastNameFeatureTip',
        image: 'shop.lobbyWidgetIcon' as NamespacedImageKeys,
      },
    },
    {
      id: 'birthDate',
      stepTitleNS: 'registration',
      stepTitleKey: 'dateOfBirthLabel',
      fields: ['birthDate'] as (keyof RegistrationData)[],
      featureTip: {
        descriptionKey: 'birthDateFeatureTip',
        image: 'common.BonanzaCoinsBank',
      },
    },
    {
      id: 'address',
      fields: ['address'] as (keyof RegistrationData)[],
      featureTip: {
        descriptionKey: 'addressFeatureTip',
        image: 'common.BonanzaPiggy',
      },
    },
    {
      id: 'terms',
      stepTitleNS: 'registration',
      stepTitleKey: 'termsPageLabel',
      stepDescriptionNS: 'registration',
      stepDescriptionKey: 'termsPageDescription',
      fields: ['marketing', 'age', 'terms'] as (keyof RegistrationData)[],
    },
    {
      id: 'summary',
      stepTitleNS: 'registration',
      stepTitleKey: 'summaryTitleLabel',
      cta: 'Submit',
    },
  ],
  fields: {
    promoId: {
      hideGenericError: true,
    },
    email: {
      placeholderNS: 'registration',
      placeholderKey: 'emailLabel',
      footerNS: 'registration',
      footerKey: 'emailFooter',
      type: 'text',
      inputMode: 'email' as InputModeOptions,
      autoComplete: 'email',
      keyboardType: 'email-address',
      textContentType: 'emailAddress',
      autoCapitalize: 'none',
    },
    password: {
      secureTextEntry: true,
      placeholderNS: 'registration',
      placeholderKey: 'passwordLabel',
      footerNS: 'registration',
      footerKey: 'passwordFooter',
      type: 'password',
      autoComplete: 'password',
      textContentType: 'password',
    },
    phone: {
      footerNS: 'registration',
      footerKey: 'phoneFooter',
      autoComplete: 'tel-national',
      textContentType: 'telephoneNumber',
      type: 'text',
      inputMode: 'tel' as InputModeOptions,
    },
    firstName: {
      placeholderNS: 'registration',
      placeholderKey: 'firstNameLabel',
      footerNS: 'registration',
      footerKey: 'firstNameFooter',
      autoComplete: 'given-name',
      textContentType: 'givenName',
    },
    middleName: {
      placeholderNS: 'registration',
      placeholderKey: 'middleNameLabel',
      autoComplete: 'additional-name',
      textContentType: 'middleName',
    },
    lastName: {
      placeholderNS: 'registration',
      placeholderKey: 'lastNameLabel',
      footerNS: 'registration',
      footerKey: 'lastNameFooter',
      autoComplete: 'family-name',
      textContentType: 'familyName',
    },
    gender: {
      headerNS: 'registration',
      headerKey: 'genderTitleLabel',
      placeholderNS: 'registration',
      placeholderKey: 'genderDescriptionLabel',
    },
    industry: {
      headerNS: 'registration',
      headerKey: 'industryTitleLabel',
      placeholderNS: 'registration',
      placeholderKey: 'industryDescriptionLabel',
    },
    occupation: {
      headerNS: 'registration',
      headerKey: 'occupationLabel',
      placeholderNS: 'registration',
      placeholderKey: 'occupationPlaceholder',
    },
    limits: {
      placeholderNS: 'registration',
      placeholderKey: 'limitsPlaceholderLabel',
    },
    marketing: {
      placeholderNS: 'registration',
      placeholderKey: 'marketingLabel',
      testID: 'registration-marketing-checkbox',
    },
    age: {
      placeholderNS: 'registration',
      placeholderKey: 'ageLabel',
      testID: 'registration-age-checkbox',
    },
    terms: {
      placeholderNS: 'registration',
      placeholderKey: 'termsLabel',
    },
    pep: {
      placeholderNS: 'registration',
      placeholderKey: 'pepLabel',
      testID: 'registration-pep-checkbox',
    },
    fit: {
      placeholderNS: 'registration',
      placeholderKey: 'fitLabel',
      testID: 'registration-fit-checkbox',
    },
  } as Record<keyof RegistrationData, FieldConfig>,
};
